import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  LogOut, 
  User, 
  Calendar, 
  FileText, 
  Settings, 
  Bell,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle
} from "lucide-react";

const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success("Erfolgreich abgemeldet");
    navigate("/login");
  };

  const quickActions = [
    {
      icon: Calendar,
      title: "Dienstplan",
      description: "Ihre aktuellen Schichten einsehen",
      color: "bg-blue-500"
    },
    {
      icon: FileText,
      title: "Dokumente",
      description: "Arbeitsverträge und Bescheinigungen",
      color: "bg-emerald-500"
    },
    {
      icon: Settings,
      title: "Einstellungen",
      description: "Profil und Benachrichtigungen",
      color: "bg-gray-500"
    }
  ];

  const notifications = [
    {
      icon: CheckCircle2,
      title: "Schicht bestätigt",
      description: "Ihre Schicht am 15.01. wurde bestätigt",
      time: "Vor 2 Stunden",
      type: "success"
    },
    {
      icon: AlertCircle,
      title: "Neue Nachricht",
      description: "Sie haben eine neue Nachricht von HR",
      time: "Vor 5 Stunden",
      type: "info"
    },
    {
      icon: Clock,
      title: "Erinnerung",
      description: "Weiterbildung am 20.01. um 09:00 Uhr",
      time: "Gestern",
      type: "warning"
    }
  ];

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="dashboard-page">
      {/* Header */}
      <header className="dashboard-header text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="bg-white/10 rounded px-3 py-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_flexipfleg-login/artifacts/ubw5qlwp_image.png" 
                  alt="FlexiHolding" 
                  className="h-6 w-auto"
                  data-testid="dashboard-logo"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="notifications-button"
              >
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-white/70">{user?.email}</p>
                </div>
              </div>
              <Button 
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/80 hover:text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold text-[#1F2937]">
            Willkommen, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#6B7280] mt-1">
            Hier ist Ihre Übersicht für heute.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2 space-y-6">
            <h2 className="text-lg font-medium text-[#1F2937]">Schnellzugriff</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {quickActions.map((action, index) => (
                <Card 
                  key={index} 
                  className="dashboard-card cursor-pointer hover:shadow-lg transition-all duration-200 border-0 bg-white"
                  data-testid={`quick-action-${action.title.toLowerCase()}`}
                >
                  <CardContent className="p-5">
                    <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center mb-4`}>
                      <action.icon className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="font-medium text-[#1F2937] mb-1">{action.title}</h3>
                    <p className="text-xs text-[#6B7280]">{action.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-0 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-[#0078D4]">12</p>
                  <p className="text-xs text-[#6B7280]">Schichten diesen Monat</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-[#10B981]">96h</p>
                  <p className="text-xs text-[#6B7280]">Arbeitsstunden</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-[#F59E0B]">3</p>
                  <p className="text-xs text-[#6B7280]">Offene Anfragen</p>
                </CardContent>
              </Card>
              <Card className="border-0 bg-white">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-semibold text-[#8B5CF6]">28</p>
                  <p className="text-xs text-[#6B7280]">Urlaubstage übrig</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Notifications Sidebar */}
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-medium text-[#1F2937]">Benachrichtigungen</h2>
              <Button variant="ghost" size="sm" className="text-[#0078D4] hover:text-[#005a9e] text-xs">
                Alle anzeigen
              </Button>
            </div>
            <Card className="border-0 bg-white">
              <CardContent className="p-0 divide-y divide-gray-100">
                {notifications.map((notification, index) => (
                  <div 
                    key={index} 
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors flex items-start space-x-3"
                    data-testid={`notification-${index}`}
                  >
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                      ${notification.type === 'success' ? 'bg-emerald-100 text-emerald-600' : ''}
                      ${notification.type === 'info' ? 'bg-blue-100 text-blue-600' : ''}
                      ${notification.type === 'warning' ? 'bg-amber-100 text-amber-600' : ''}
                    `}>
                      <notification.icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1F2937]">{notification.title}</p>
                      <p className="text-xs text-[#6B7280] truncate">{notification.description}</p>
                      <p className="text-xs text-[#9CA3AF] mt-1">{notification.time}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-1" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
